import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Target, ExternalLink, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  skills: string[];
  experience_required: number;
  status: string;
}

interface ScreeningSession {
  id: string;
  transcript?: string;
  duration_seconds?: number;
  created_at: string;
}

interface JobMatchRecommendationsProps {
  session: ScreeningSession;
}

// Extract skills and experience from transcript
const extractCandidateProfile = (transcript: string) => {
  const skills: string[] = [];
  let experienceYears = 0;

  const lines = transcript.split('\n');
  
  // Common tech skills to look for
  const skillKeywords = [
    'JavaScript', 'React', 'Python', 'Java', 'TypeScript', 'Node.js', 'SQL', 
    'AWS', 'Docker', 'Kubernetes', 'Angular', 'Vue', 'MongoDB', 'PostgreSQL',
    'Git', 'HTML', 'CSS', 'Redux', 'GraphQL', 'REST API', 'Microservices'
  ];

  for (const line of lines) {
    // Extract skills
    for (const skill of skillKeywords) {
      if (line.toLowerCase().includes(skill.toLowerCase()) && !skills.includes(skill)) {
        skills.push(skill);
      }
    }

    // Extract experience years
    const expMatch = line.match(/(\d+)\s+year[s]?\s+(?:of\s+)?experience/i);
    if (expMatch && experienceYears === 0) {
      experienceYears = parseInt(expMatch[1]);
    }
  }

  return { skills, experienceYears };
};

// Calculate match score between candidate and job
const calculateMatchScore = (candidateSkills: string[], candidateExp: number, job: Job) => {
  let skillsScore = 0;
  let experienceScore = 0;

  // Skills matching (70% weight)
  if (job.skills && job.skills.length > 0) {
    const matchingSkills = candidateSkills.filter(skill => 
      job.skills.some(jobSkill => 
        jobSkill.toLowerCase().includes(skill.toLowerCase()) ||
        skill.toLowerCase().includes(jobSkill.toLowerCase())
      )
    );
    skillsScore = (matchingSkills.length / job.skills.length) * 70;
  }

  // Experience matching (30% weight)
  if (job.experience_required && candidateExp > 0) {
    if (candidateExp >= job.experience_required) {
      experienceScore = 30;
    } else {
      experienceScore = (candidateExp / job.experience_required) * 30;
    }
  } else {
    experienceScore = 15; // Default if no experience data
  }

  return Math.round(skillsScore + experienceScore);
};

export const JobMatchRecommendations = ({ session }: JobMatchRecommendationsProps) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [matches, setMatches] = useState<Array<Job & { matchScore: number; matchingSkills: string[] }>>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const candidateProfile = session.transcript ? extractCandidateProfile(session.transcript) : null;

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('jobs')
          .select('id, title, company, location, skills, experience_required, status')
          .eq('status', 'open')
          .limit(10);

        if (error) throw error;

        const jobsWithMatches = (data || []).map(job => {
          const matchScore = candidateProfile ? 
            calculateMatchScore(candidateProfile.skills, candidateProfile.experienceYears, job) : 0;
          
          const matchingSkills = candidateProfile ? 
            candidateProfile.skills.filter(skill => 
              job.skills?.some(jobSkill => 
                jobSkill.toLowerCase().includes(skill.toLowerCase()) ||
                skill.toLowerCase().includes(jobSkill.toLowerCase())
              )
            ) : [];

          return { ...job, matchScore, matchingSkills };
        });

        // Sort by match score and take top matches
        const sortedMatches = jobsWithMatches
          .filter(job => job.matchScore > 30) // Only show decent matches
          .sort((a, b) => b.matchScore - a.matchScore)
          .slice(0, 3);

        setJobs(data || []);
        setMatches(sortedMatches);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [session, candidateProfile]);

  const getMatchColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getMatchBadgeVariant = (score: number) => {
    if (score >= 80) return "default";
    if (score >= 60) return "secondary";
    return "outline";
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Target className="h-5 w-5 text-primary" />
            Job Match Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading job matches...</p>
        </CardContent>
      </Card>
    );
  }

  if (!candidateProfile || candidateProfile.skills.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Target className="h-5 w-5 text-primary" />
            Job Match Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Unable to extract sufficient candidate information from the screening transcript for job matching.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (matches.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Target className="h-5 w-5 text-primary" />
            Job Match Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            No strong job matches found based on the screening results. Consider expanding the candidate's skill profile or reviewing open positions.
          </p>
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">Extracted candidate profile:</p>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-muted-foreground">Skills: </span>
                <span className="text-sm">{candidateProfile.skills.join(', ')}</span>
              </div>
              {candidateProfile.experienceYears > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground">Experience: </span>
                  <span className="text-sm">{candidateProfile.experienceYears} years</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const topMatch = matches[0];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Target className="h-5 w-5 text-primary" />
          Job Match Recommendations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Top Recommendation */}
        {topMatch.matchScore >= 75 && (
          <div className="p-4 border-2 border-primary/20 rounded-lg bg-primary/5">
            <div className="flex items-center gap-2 mb-3">
              <Star className="h-5 w-5 text-primary" />
              <span className="font-semibold text-primary">Top Recommendation</span>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold">{topMatch.title}</h4>
                  <p className="text-sm text-muted-foreground">{topMatch.company} • {topMatch.location}</p>
                </div>
                <Badge variant={getMatchBadgeVariant(topMatch.matchScore)} className={getMatchColor(topMatch.matchScore)}>
                  {topMatch.matchScore}% match
                </Badge>
              </div>
              
              <Progress value={topMatch.matchScore} className="h-2" />
              
              {topMatch.matchingSkills.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Matching Skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {topMatch.matchingSkills.map((skill, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <Button 
                onClick={() => navigate(`/jobs/${topMatch.id}`)}
                className="w-full"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Job Details
              </Button>
            </div>
          </div>
        )}

        {/* Other Matches */}
        {matches.slice(topMatch.matchScore >= 75 ? 1 : 0).length > 0 && (
          <div>
            <h4 className="font-medium mb-3">Other Potential Matches</h4>
            <div className="space-y-3">
              {matches.slice(topMatch.matchScore >= 75 ? 1 : 0).map((match) => (
                <div key={match.id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium">{match.title}</h5>
                      <p className="text-sm text-muted-foreground">{match.company}</p>
                    </div>
                    <Badge variant={getMatchBadgeVariant(match.matchScore)} className={getMatchColor(match.matchScore)}>
                      {match.matchScore}%
                    </Badge>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/jobs/${match.id}`)}
                  >
                    View Details
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Candidate Profile Summary */}
        <div className="mt-6 p-3 bg-muted/50 rounded-lg">
          <h4 className="font-medium mb-2">Extracted Candidate Profile</h4>
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Skills: </span>
              <span>{candidateProfile.skills.join(', ')}</span>
            </div>
            {candidateProfile.experienceYears > 0 && (
              <div>
                <span className="text-muted-foreground">Experience: </span>
                <span>{candidateProfile.experienceYears} years</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
